import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './IDVerification.css';
import { ArrowForwardRounded, BadgeOutlined, CheckCircleOutlineRounded, VerifiedUserOutlined } from '@mui/icons-material';
import { useIdentification } from './IdentificationContext';
import {
  EnterpriseButton,
  EnterpriseCard,
  EnterpriseGrid,
  EnterpriseHero,
  EnterpriseShell,
  EnterpriseTopBar,
  WarningBanner
} from './ui/EnterpriseLayout';

function IDVerification() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const step1Complete = searchParams.get('step') === '1-complete';
  const [showWarning, setShowWarning] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState(8);
  const { createIdentification } = useIdentification();
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (!showWarning || !step1Complete) return;
    
    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [showWarning, step1Complete]);

  return (
    <EnterpriseShell>
      <EnterpriseTopBar right={<span className="vt-chip vt-chip--subtle">ID verification</span>} />

      <EnterpriseHero
        title="Confirm your age"
        subtitle="To complete verification, we'll capture your document and then a quick face scan."
      />

      <EnterpriseGrid columns={1}>
        <EnterpriseCard>
          {step1Complete && showWarning && (
            <div style={{ marginBottom: 14 }}>
              <WarningBanner onClose={() => setShowWarning(false)}>
                For security reasons this session will expire in {timeRemaining} minutes.
              </WarningBanner>
            </div>
          )}

          <ul className="vt-list">
            <li className="vt-listItem">
              {step1Complete ? (
                <CheckCircleOutlineRounded className="vt-listIcon" />
              ) : (
                <BadgeOutlined className="vt-listIcon" />
              )}
              <span>Document capture (passport, driving licence, national ID)</span>
            </li>
            <li className="vt-listItem">
              <VerifiedUserOutlined className="vt-listIcon" />
              <span>Quick face scan for liveness and match</span>
            </li>
          </ul>

          <div className="vt-actions">
            {step1Complete ? (
              <EnterpriseButton variant="primary" onClick={() => navigate('/consent')}>
                <span>Continue</span>
                <ArrowForwardRounded className="vt-btnIcon" />
              </EnterpriseButton>
            ) : (
              <EnterpriseButton
                variant="primary"
                onClick={async () => {
                  try {
                    setIsCreating(true);
                    await createIdentification();
                    navigate('/capture-document');
                  } catch (error) {
                    console.error('Failed to create identification:', error);
                    alert('Failed to start verification. Please try again.');
                  } finally {
                    setIsCreating(false);
                  }
                }}
                disabled={isCreating}
              >
                <span>{isCreating ? 'Starting…' : 'Get started'}</span>
                <ArrowForwardRounded className="vt-btnIcon" />
              </EnterpriseButton>
            )}

            <EnterpriseButton variant="secondary" type="button" onClick={() => navigate('/')}>
              Back to options
            </EnterpriseButton>
          </div>
        </EnterpriseCard>
      </EnterpriseGrid>
    </EnterpriseShell>
  );
}

export default IDVerification;
